import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import styled, { keyframes, useTheme, type DefaultTheme } from 'styled-components';
import type { CSSProperties } from 'react';
import { BsSearch } from 'react-icons/bs'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useIntl, FormattedMessage } from "react-intl";

import { Input, Link } from './StyledComponents';
import LoginModal from './components/LoginModal'
import SignupModal from './components/SignupModal'
import ForgotPasswordModal from './components/ForgotPasswordModal'
import { loggedInContext } from './auth/AuthContext'
import type { User } from './auth/AuthContext'
import useWindowSize from './useWindowSize'
import useTenant from './client/useTenant'
import { useCsEvent } from './client/useEvents'
import HamburgerMenu from './components/HamburgerMenu';
import {
  useSearchSuggestions,
  SuggestionsDropdown,
  type SearchSuggestion,
} from './components/SearchAutocomplete';
import queryString from 'query-string';
import { getSearchPathPrefix } from './utils/searchPath';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeaderOuter = styled.header<{ $scrolled?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({theme}) => theme.colors.headerBg};
  border-bottom: 1px solid ${({theme}) => theme.colors.headerBorder};
  box-shadow: ${({ theme, $scrolled }) =>
    $scrolled
      ? (theme.shadows?.elevated ?? '0 4px 16px rgba(0, 0, 0, 0.12)')
      : theme.colors.headerShadow};
  transition: box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
`;

const HeaderInner = styled.div<{ $scrolled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 ${({ $scrolled }) => ($scrolled ? '16px' : '12px')};
  height: ${({ $scrolled }) => ($scrolled ? '52px' : '56px')};
  transition: height 0.2s ease, padding 0.2s ease;

  @media (min-width: 576px) {
    gap: 16px;
    height: ${({ $scrolled }) => ($scrolled ? '52px' : '64px')};
    padding: 0 ${({ $scrolled }) => ($scrolled ? '16px' : '24px')};
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  color: ${({theme}) => theme.colors.primary};
`;

const LogoImg = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  transition: transform 0.2s ease;

  ${LogoLink}:hover & {
    transform: scale(1.05);
  }

  @media (min-width: 576px) {
    width: 36px;
    height: 36px;
  }
`;

const LogoText = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${({theme}) => theme.colors.primary};
  white-space: nowrap;

  @media (max-width: 420px) {
    display: none;
  }
`;

const SearchArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 0;
  position: relative;
  z-index: 100;

  @media (min-width: 576px) {
    margin: 0 8px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 600px;
  background: ${({theme}) => theme.colors.searchBg};
  border: 1.5px solid ${({theme}) => theme.colors.searchBorder};
  border-radius: 50rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:focus-within {
    border-color: ${({theme}) => theme.colors.accent};
    box-shadow: 0 0 0 3px ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 213, 79, 0.15)' : 'rgba(212, 168, 67, 0.12)'};
    background: ${({theme}) => theme.colors.searchFocusBg};
  }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  color: ${({theme}) => theme.colors.accent};
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 50rem 0 0 50rem;
  transition: color 0.2s;

  &:hover {
    color: ${({theme}) => theme.colors.accentHover};
  }

  @media (min-width: 576px) {
    width: 42px;
    height: 40px;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  border: none;
  background: transparent;
  padding: 6px 2px;
  font-size: 0.9rem;
  outline: none;
  min-width: 0;
  color: ${({theme}) => theme.colors.text};

  &::placeholder {
    color: ${({theme}) => theme.colors.textMuted};
  }

  @media (min-width: 576px) {
    padding: 8px 4px;
    font-size: 0.95rem;
  }
`;

const FilterToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 2px;
  background: none;
  border: none;
  color: ${({theme}) => theme.colors.textMuted};
  cursor: pointer;
  flex-shrink: 0;
  border-left: 1px solid ${({theme}) => theme.colors.searchBorder};
  border-radius: 0 50rem 50rem 0;
  transition: color 0.2s, background 0.2s;

  &:hover {
    color: ${({theme}) => theme.colors.primary};
    background: ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  }

  &:after {
    display: inline-block;
    content: "";
    border-top: .3em solid;
    border-right: .3em solid transparent;
    border-bottom: 0;
    border-left: .3em solid transparent;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const EkMenuPanel = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: ${({theme}) => theme.colors.surface};
  border-radius: 0 0 16px 16px;
  box-shadow: ${({theme}) => theme.shadows?.elevated ?? 'none'};
  padding: 24px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${slideDown} 0.2s ease;
`;

const EkMenuIntro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 15px;
`;

const EkMenuFieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
`;

const EkMenuField = styled.div`
  flex: 1;
  min-width: 180px;
`;

const EkMenuLabel = styled.label`
  font-weight: 500;
  margin-bottom: 6px;
  display: block;
  color: ${({ theme }) => theme.colors.text};
`;

const ekMenuControlStyle = (theme: DefaultTheme): CSSProperties => ({
  width: '100%',
  borderRadius: 8,
  border: `1px solid ${theme.colors.inputBorder}`,
  fontSize: 15,
  padding: '6px 10px',
  background: theme.colors.inputBg,
  color: theme.colors.inputText,
});

const EkMenuActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const EkMenuSubmitBtn = styled.input`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverse};
  border: none;
  font-weight: 600;
  padding: 8px 22px;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const EkMenuCancelBtn = styled.input`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 600;
  padding: 8px 22px;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

interface Gazete {
  gazete: string;
  dil: string;
  gorunen_ad: string;
  logo: string;
}

interface GazetelerQueryData {
  gazeteler: Gazete[];
}

const gqlQuery = gql`
  query Gazeteler {
    gazeteler {
      gazete,
      dil,
      gorunen_ad,
      logo
    }
  }
`

interface LoggedInContextValue {
  loggedIn: boolean;
  user: User | null;
  isLoginOpen: boolean;
  isSignupOpen: boolean;
  openLoginModal: (cta?: React.ReactNode) => void;
  openSignupModal: () => void;
  closeAllModals: () => void;
}

export function Header(_props: Record<string, never>) {
  const theme = useTheme()
  const location = useLocation()
  const history = useHistory()
  const windowSize = useWindowSize()
  const intl = useIntl()
  const match = useRouteMatch<{ query: string }>("/ara/:query")
  const { data } = useQuery<GazetelerQueryData>(gqlQuery, { ssr: false })
  const {
    loggedIn,
    user,
    isLoginOpen,
    isSignupOpen,
    openLoginModal,
    openSignupModal,
    closeAllModals
  } = React.useContext(loggedInContext) as LoggedInContextValue
  const tenant = useTenant()
  const fireCsEvent = useCsEvent()

  const araInput = useRef<HTMLInputElement | null>(null)
  const matchQuery = match?.params?.query ?? ""
  const setAraInput = useCallback((node: HTMLInputElement | null) => {
    araInput.current = node
    if (node) {
      node.value = matchQuery
    }
  }, [matchQuery])
  const gazeteSelect = useRef<HTMLSelectElement | null>(null)
  const setGazeteSelect = useCallback((node: HTMLSelectElement | null) => {
    gazeteSelect.current = node
    const queryParams = queryString.parse(location.search)
    if (queryParams.gazete && node) {
      node.value = queryParams.gazete as string
    }
  }, [location.search])
  const startDatePicker = useRef<HTMLInputElement | null>(null)
  const setStartDatePicker = useCallback((node: HTMLInputElement | null) => {
    const queryParams = queryString.parse(location.search)
    startDatePicker.current = node
    if (queryParams.startDate && node) {
      node.value = queryParams.startDate as string
    }
  }, [location.search])
  const endDatePicker = useRef<HTMLInputElement | null>(null)
  const setEndDatePicker = useCallback((node: HTMLInputElement | null) => {
    const queryParams = queryString.parse(location.search)
    endDatePicker.current = node
    if (queryParams.endDate && node) {
      node.value = queryParams.endDate as string
    }
  }, [location.search])
  const esnekAra = useRef<HTMLInputElement | null>(null)
  const setEsnekAra = useCallback((node: HTMLInputElement | null) => {
    const queryParams = queryString.parse(location.search)
    esnekAra.current = node
    if (queryParams.esnek && node) {
      node.checked = true
    }
  }, [location.search])
  const [isOpen, setIsOpen] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const ara = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!araInput.current?.value) return

    fireCsEvent({
      category: 'Arama',
      action: 'Submit',
      label: 'Kullanıcı arama yapıyor'
    })

    const url = new URL(`/${getSearchPathPrefix(tenant)}/${araInput.current.value}`,
      window.location.href)
    if (startDatePicker.current?.value) {
      url.searchParams.set("startDate", startDatePicker.current.value)
    }
    if (endDatePicker.current?.value) {
      url.searchParams.set("endDate", endDatePicker.current.value)
    }
    if (esnekAra.current?.checked) {
      url.searchParams.set("esnek", 't')
    }
    if (gazeteSelect.current?.value) {
      url.searchParams.set("gazete", gazeteSelect.current.value)
    }
    history.push({ pathname: url.pathname, search: url.search })
  }, [history, tenant, fireCsEvent])

  const toggleEkMenu = useCallback((e: React.MouseEvent) => {
    if (!isOpen) {
      fireCsEvent({
        category: 'Arama',
        action: 'Engagement',
        label: 'Ek menü açıldı'
      })
    }
    e.preventDefault()
    setIsOpen(!isOpen)
  }, [isOpen, fireCsEvent])

  const handleSuggestionSelect = useCallback((suggestion: SearchSuggestion) => {
    fireCsEvent({ category: 'Arama', action: 'Suggestion', label: `Suggestion selected: ${suggestion.category}` })
    if (suggestion.category === 'newspapers') {
      history.push(`/gazete/${suggestion.value}`)
    } else if (suggestion.category === 'tags') {
      history.push(`/etiket/${suggestion.text}`)
    } else {
      if (araInput.current) araInput.current.value = suggestion.text
      history.push(`/${getSearchPathPrefix(tenant)}/${suggestion.text}`)
    }
  }, [history, tenant, fireCsEvent])

  const {
    suggestions: headerSuggestions,
    isOpen: suggestionsOpen,
    activeIndex,
    handleInput: handleSuggestInput,
    handleKeyDown: handleSuggestKeyDown,
    handleSelect: handleSuggestSelect,
    handleBlur: handleSuggestBlur,
    handleFocus: handleSuggestFocus,
  } = useSearchSuggestions({ onSelect: handleSuggestionSelect })

  const onAccountClick = () => {
    fireCsEvent({
      category: "Header",
      action: "Engagement",
      label: "Account button click"
    })
    if (!loggedIn) {
      openLoginModal()
    } else {
      history.push("/account")
    }
  }

  const gazeteler = data?.gazeteler?.slice()?.sort((gazete1, gazete2) => {
    return gazete1.gorunen_ad > gazete2.gorunen_ad ? 1 :
      gazete1.gorunen_ad < gazete2.gorunen_ad ? -1 : 0
  })

  return (
    <>
      <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
      <HeaderOuter role="banner" $scrolled={scrolled}>
        {isLoginOpen && <LoginModal
          onClose={() => closeAllModals()} onForgotPassword={() => {
            closeAllModals()
            fireCsEvent({
              category: 'Header',
              action: 'Engagement',
              label: 'Forgot password clicked'
            })
            setShowForgot(true)
          }} />}
        {isSignupOpen && <SignupModal onClose={() => closeAllModals()} />}

        <HeaderInner $scrolled={scrolled}>
          <LogoLink to="/" rel="home"
            onClick={() => {
              fireCsEvent({
                category: 'Header',
                action: 'Navigation',
                label: 'Logoya basıldı'
              })
            }}>
            <LogoImg src="/static/favicon.png" alt="" />
            <LogoText>
              <FormattedMessage id="name"/>
            </LogoText>
          </LogoLink>

          <SearchArea>
            <SearchForm method="get" target="_top"
              action={process.env.REACT_APP_GA_BASE_URL + `/${getSearchPathPrefix(tenant)}`}
              onSubmit={ara}>
              <SearchBtn type="submit" aria-label="Ara">
                <BsSearch />
              </SearchBtn>
              <SearchInput type="search"
                name="q"
                ref={setAraInput}
                placeholder={intl.formatMessage({ id: "ara.cta", defaultMessage: "Gazetelerde arayın" })}
                onInput={handleSuggestInput}
                onKeyDown={handleSuggestKeyDown}
                onBlur={handleSuggestBlur}
                onFocus={handleSuggestFocus}
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestionsOpen}
                aria-controls="search-suggestions-listbox" />
              <FilterToggle type="button" onClick={toggleEkMenu} aria-label="Filtreler" />
            </SearchForm>
            <SuggestionsDropdown
              suggestions={headerSuggestions}
              isOpen={suggestionsOpen}
              activeIndex={activeIndex}
              onSelect={handleSuggestSelect}
            />
          </SearchArea>

          <HeaderActions>
            <HamburgerMenu
              loggedIn={loggedIn}
              user={user}
              openLoginModal={openLoginModal}
              openSignupModal={openSignupModal}
              closeAllModals={closeAllModals}
              onAccountClick={onAccountClick}
              windowWidth={windowSize.width ?? 1024}
            />
          </HeaderActions>
        </HeaderInner>

        {isOpen && (
          <EkMenuPanel>
            <div style={{ marginBottom: 8 }}>
              <EkMenuIntro><FormattedMessage id="ara.aciklama" /></EkMenuIntro>
            </div>
            <EkMenuFieldRow>
              <EkMenuField>
                <EkMenuLabel htmlFor="gazete">
                  <FormattedMessage id="ara.gazete" defaultMessage="Gazete:" />
                </EkMenuLabel>
                <select id="gazete" name="Gazete" ref={setGazeteSelect}
                  style={ekMenuControlStyle(theme)}>
                  <option value=""><FormattedMessage id="header.chooseOption" defaultMessage="--Please choose an option--" /></option>
                  {gazeteler?.map(gazete => (
                    <option key={gazete.gazete} value={gazete.gazete}>{gazete.gorunen_ad}</option>
                  ))}
                </select>
              </EkMenuField>
              <EkMenuField>
                <EkMenuLabel htmlFor="start">
                  <FormattedMessage id="ara.fromDate" defaultMessage="Şu tarihten:" />
                </EkMenuLabel>
                <Input type="date" id="start" ref={setStartDatePicker} name="Şu tarihten" min="1850-01-01"
                  style={ekMenuControlStyle(theme)} />
              </EkMenuField>
              <EkMenuField>
                <EkMenuLabel htmlFor="end">
                  <FormattedMessage id="ara.endDate" defaultMessage="Şu tarihe:" />
                </EkMenuLabel>
                <Input type="date" id="end" ref={setEndDatePicker} name="Şu tarihe" min="1850-01-01"
                  style={ekMenuControlStyle(theme)} />
              </EkMenuField>
            </EkMenuFieldRow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 8 }}>
              <EkMenuLabel htmlFor="esnek" style={{ marginBottom: 0 }}>
                <FormattedMessage id="header.flexibleSearch" defaultMessage="Esnek ara:" />
              </EkMenuLabel>
              <Input type="checkbox" id="esnek" ref={setEsnekAra} name="esnek" style={{ marginLeft: 8, width: 18, height: 18 }} />
            </div>
            <EkMenuActions>
              <EkMenuSubmitBtn type="button"
                value={intl.formatMessage({ id: "ara.button", defaultMessage: "Ara" })} onClick={ara}
              />
              <EkMenuCancelBtn type="button"
                value={intl.formatMessage({ id: "ara.cancel", defaultMessage: "Boşver" })} onClick={toggleEkMenu}
              />
            </EkMenuActions>
          </EkMenuPanel>
        )}
      </HeaderOuter>
    </>
  )
}
